'use client'

import { cn, InfoTooltip, Text } from '@repo/ui'
import { useEffect, useState } from 'react'

import {
  CANDIDATES_EXPIRY_LABEL,
  CANDIDATES_EXPIRY_MS,
  CANDIDATES_SEVERE_WITHIN_MS,
  CANDIDATES_WARNING_AFTER_MS
} from '../candidates.freshness'

const TICK_MS = 15_000

function formatAge(ageMs: number): string {
  const totalSecs = Math.max(0, Math.floor(ageMs / 1000))
  if (totalSecs < 60) return `${totalSecs}s ago`
  const totalMins = Math.floor(totalSecs / 60)
  if (totalMins < 60) return `${totalMins}m ago`
  return `${Math.floor(totalMins / 60)}h ${totalMins % 60}m ago`
}

/**
 * "within about a minute", not "on every request": the request itself is uncached, but the eRPC
 * failover transport serves `eth_call` with `maxAge: 60`, so a verdict can be a minute behind chain
 * state when the primary provider is failing. "Load" rather than "request" because a load is the unit
 * the reader can observe.
 */
const BASE_TOOLTIP =
  'Amounts are re-read from the chain on every load, within about a minute of current state. This is the age of the borrower and market list being checked.'

type Severity = 'ok' | 'warning' | 'severe' | 'expired'

/**
 * Age is the only thing this component observes, so the copy claims nothing beyond it. Revalidation
 * in `@repo/cache` is triggered *by reads*, not by a scheduler, so on a quiet day age grows with
 * nothing broken — a suffix asserting "refresh failing" would cry wolf on the first pageview after
 * an idle stretch, and a signal that false-alarms is one that gets ignored when it matters.
 */
function describe(severity: Severity, ageMs: number) {
  switch (severity) {
    case 'expired':
      return {
        suffix: ' — refresh required',
        tooltip: `${BASE_TOOLTIP} This list is past its ${CANDIDATES_EXPIRY_LABEL} limit, so it can no longer be served; refresh to recompute it.`,
        className: 'text-destructive-foreground'
      }
    case 'severe': {
      const minsLeft = Math.max(0, Math.ceil((CANDIDATES_EXPIRY_MS - ageMs) / 60_000))
      return {
        suffix: ` — expires in ${minsLeft}m`,
        tooltip: `${BASE_TOOLTIP} This list reaches its ${CANDIDATES_EXPIRY_LABEL} limit shortly; after that the console cannot show candidates until discovery recovers.`,
        className: 'text-destructive-foreground'
      }
    }
    case 'warning':
      return {
        suffix: ' — new borrowers may be missing',
        tooltip: `${BASE_TOOLTIP} The list is older than its refresh window, so borrowers that became at-risk since then may be missing.`,
        className: 'text-warning-foreground'
      }
    case 'ok':
      return { suffix: '', tooltip: BASE_TOOLTIP, className: 'text-typography-secondary' }
  }
}

type CandidateListAgeProps = { updatedAt: number }

/**
 * Age of the cached borrower/market candidate list.
 *
 * Labelled "Candidate list", never "Last updated": the amounts in each row are recomputed from fresh
 * on-chain state on every request, and only the *list of pairs to check* is cached. A bare timestamp
 * in a data table reads as the age of the numbers, which would be wrong here — while during a Morpho
 * API outage the opposite claim ("Live") would also be wrong, since newly at-risk borrowers can be
 * missing. Severity is carried by the text suffix as well as color, so color is never the only signal.
 */
export function CandidateListAge({ updatedAt }: CandidateListAgeProps) {
  // Re-render on a timer so the relative age advances without a refetch.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [])

  const ageMs = Math.max(0, now - updatedAt)
  const severity: Severity =
    ageMs >= CANDIDATES_EXPIRY_MS
      ? 'expired'
      : ageMs >= CANDIDATES_EXPIRY_MS - CANDIDATES_SEVERE_WITHIN_MS
        ? 'severe'
        : ageMs >= CANDIDATES_WARNING_AFTER_MS
          ? 'warning'
          : 'ok'
  const { suffix, tooltip, className } = describe(severity, ageMs)

  return (
    <InfoTooltip
      content={tooltip}
      trigger={
        <Text size="xs" className={cn('whitespace-nowrap', className)}>
          {`Candidate list · ${formatAge(ageMs)}${suffix}`}
        </Text>
      }
      showInfoIcon
    />
  )
}
